import { addToQuery, showQuery } from "./handwriting-query.js";
import { offerCandidates } from "./handwriting-candidates.js";
import { wirePanel } from "./handwriting-wiring.js";
import { handwritingDialog } from "./handwriting-markup.js";
import type { HandwritingElements } from "./handwriting-markup.js";
import { HandwritingPad } from "./handwriting-pad.js";
import type { HandwritingSource } from "./handwriting-source.js";
import type { SearchBox } from "./search-box.js";

/**
 * The pad, the candidates and the box they go into.
 *
 * One panel per page rather than one per search box. A 词典 page draws a box on
 * the navigation bar, another in the rail and another in the article, and all
 * three open this. Which box a candidate goes into is decided when the panel is
 * opened. See `handwriting-start.ts`.
 *
 * **A candidate is added to the box, and the dialog stays open.** That is the
 * one thing here that MDBG's pad does differently, and it is deliberate. Most
 * 词典 queries are two characters, so a reader who has to reopen the pad
 * between them writes the first, loses the square, finds the button again and
 * writes the second. Here they write 汉, press it, write 字, press it and press
 * Search. The box fills up behind the dialog and the line above the button
 * shows them what they have so far.
 *
 * **The lookup runs on the strokes, not on a settle.** Every stroke costs one
 * scan of 9,276 characters, which is about two milliseconds, so there is
 * nothing here worth debouncing. A reader lifting their pen sees the list move
 * before they have put it down again.
 */

/** What the panel needs to do its work. */
export interface HandwritingPanelProperties {
  /** The page to build the dialog in. */
  readonly page: Document;

  /** Where the index comes from. */
  readonly source: HandwritingSource;

  /**
   * The box the pen that opened this belongs to.
   *
   * Given at construction as well as at every {@link HandwritingPanel.open},
   * because the panel is built by the press that opens it and there has never
   * been a moment when it had no box. Holding it as something that might be
   * missing put a guard on every use of it that nothing could reach.
   */
  readonly box: SearchBox;
}

/** The dialog, and everything that happens in it. */
export class HandwritingPanel {
  readonly #elements: HandwritingElements;
  readonly #pad: HandwritingPad;
  readonly #source: HandwritingSource;
  #box: SearchBox;

  constructor(properties: HandwritingPanelProperties) {
    const { page, source } = properties;

    this.#source = source;
    this.#box = properties.box;
    this.#elements = handwritingDialog(page);
    this.#pad = new HandwritingPad({
      canvas: this.#elements.canvas,
      onChange: (): void => {
        void this.#offerCandidates();
      },
    });

    page.body.append(this.#elements.dialog);
    wirePanel({ page, elements: this.#elements, pad: this.#pad, panel: this });
  }

  /** Runs the search the reader has built up, and closes the dialog. */
  search(): void {
    this.#elements.dialog.close();
    this.#box.form.requestSubmit();
  }

  /** Puts the reader's cursor back where they left it. */
  restoreFocus(): void {
    this.#box.input.focus();
  }

  /**
   * Opens the pad over a box, and puts what a reader picks into that box.
   *
   * The index is asked for here rather than on the first stroke, so the fetch
   * runs while the reader is drawing and has usually finished before they lift
   * their hand. A reader who is faster than the network sees the message under
   * the square until it arrives.
   */
  open(box: SearchBox): void {
    this.#box = box;
    this.#pad.clear();
    showQuery(this.#elements, this.#box);
    this.#elements.dialog.showModal();

    // After the dialog is open, and not before: a canvas inside a closed
    // dialog has no box to measure, so a pad sized then comes out one pixel
    // square and every stroke lands in the same corner.
    this.#pad.resize();
    void this.#source.index();
  }

  /** Adds one character to the box, and leaves the square ready for the next. */
  #take(character: string): void {
    addToQuery(this.#box, character);
    this.#pad.clear();
    showQuery(this.#elements, this.#box);
  }

  /** Draws the characters the reader may have written. */
  async #offerCandidates(): Promise<void> {
    await offerCandidates({
      row: this.#elements,
      pad: this.#pad,
      source: this.#source,
      take: this.#take.bind(this),
    });
  }
}
